import { Link } from "react-router-dom";
import { ResourceStructuredData } from "./ResourceArticlePage";
import "./WhySchedulingImportant.css";

const CONTENTS = [
  ["consistency", "Show up consistently"],
  ["timing", "Reach people at the right time"],
  ["review", "Make room for a second look"],
  ["visibility", "Keep your storefront active"],
  ["system", "Build a system, not a reminder"],
];

/** Editorial version of docs/marketing/WhyScheduling.md. */
export function WhySchedulingImportant() {
  return (
    <article className="scheduling-story">
      <ResourceStructuredData article={{ slug: "why-scheduling", title: "Why social media scheduling matters", description: "Discover how scheduling helps you post consistently, avoid last-minute mistakes, and keep your brand visible." }} />
      <header className="hero">
        <div className="hero-inner">
          <div className="hero-copy">
            <Link className="scheduling-back" to="/resources">← All resources</Link>
            <p className="hero-badge"><span aria-hidden="true">◷</span> Why scheduling matters</p>
            <h1>Your customers<br />are scrolling.<span className="hero-gradient-text">Even when<br />you’re busy.</span></h1>
            <p className="lede">Social media moves fast. Scheduling helps your brand stay present while you focus on running your business.</p>
            <div className="hero-cta">
              <a href="#consistency" className="btn-link primary lg">Read the story <span aria-hidden="true">↓</span></a>
              <Link to="/how-it-works" className="btn-link lg">See how it works <span aria-hidden="true">→</span></Link>
            </div>
            <p className="scheduling-byline">Simino Social <span aria-hidden="true">·</span> Social media planning</p>
          </div>
          <div className="scheduling-preview" role="img" aria-label="Illustrative schedule: a morning story at 8 AM, a lunch update at 12:30 PM, and an evening video at 7 PM, planned ahead while you run your business.">
            <div aria-hidden="true">
              <div className="scheduling-preview-head"><img src="/mark.png" alt="" /><div><span className="eyebrow">Your content, planned</span><h2>A day that keeps moving.</h2></div></div>
              <div className="scheduling-timeline">
                {[
                  ["8:00 AM", "◎", "Morning story", "While you open up", "violet"],
                  ["12:30 PM", "f", "Lunchtime update", "While you meet a customer", "blue"],
                  ["7:00 PM", "▶", "Evening video", "While you switch off", "green"],
                ].map(([time, icon, title, caption, color]) => (
                  <div className="scheduling-slot" key={time}><time>{time}</time><div className="scheduling-post"><i className={`benefit-icon ${color}`}>{icon}</i><div><strong>{title}</strong><span>{caption}</span></div><span className="scheduling-check">✓</span></div></div>
                ))}
              </div>
              <div className="scheduling-preview-note"><span>◷</span><div><strong>You run your business.</strong><p>Your content has a plan.</p></div></div>
              <p className="scheduling-example">Illustrative schedule · Your timing, your choice</p>
            </div>
          </div>
        </div>
      </header>

      <div className="band scheduling-intro">
        <div className="band-inner narrow">
          <p className="eyebrow">A little planning. A more consistent presence.</p>
          <p className="lede">Your customers may be online at 8 AM, during lunch, late at night, or while you’re busy running your business. If your brand only posts when someone remembers, you may be invisible at the exact moment your audience is ready to discover, engage, or buy.</p>
          <p>And while your account stays quiet, your competitors may be showing up.</p>
          <p><strong>That is why social media scheduling is no longer just a convenience.</strong> It is part of running a consistent, professional brand.</p>
        </div>
      </div>

      <div className="band alt">
        <div className="band-inner scheduling-reading-grid">
          <aside className="scheduling-aside">
            <nav aria-label="In this article">
              <p className="eyebrow">In this article</p>
              <ol>{CONTENTS.map(([id, label]) => <li key={id}><a href={`#${id}`}>{label}</a></li>)}</ol>
              <div className="scheduling-aside-note"><span aria-hidden="true">✦</span><p>Prepare when it works for you.<br /><strong>Publish when it works for them.</strong></p></div>
            </nav>
          </aside>

          <div className="scheduling-prose">
            <section id="consistency" aria-labelledby="consistency-title">
              <span className="scheduling-number" aria-hidden="true">01 / CONSISTENCY</span>
              <h2 id="consistency-title">Inconsistent posting can make your brand disappear.</h2>
              <p>You create a great post.</p>
              <div className="scheduling-interruptions"><span>A customer calls.</span><span>A meeting runs late.</span><span>Something urgent comes up.</span></div>
              <p><strong>The post never gets published.</strong></p>
              <p>One missed post may not matter. But when it happens repeatedly, your social presence becomes inconsistent. Your audience hears from you less often, campaigns lose momentum, and potential customers may start noticing other brands instead.</p>
              <p>Scheduling helps prevent that.</p>
              <div className="scheduling-takeaway"><span aria-hidden="true">↗</span><p>Plan your content once and let it go live automatically at the time you choose.</p></div>
            </section>

            <section id="timing" aria-labelledby="timing-title">
              <span className="scheduling-number" aria-hidden="true">02 / TIMING</span>
              <h2 id="timing-title">Posting at the wrong time can waste great content.</h2>
              <p>A strong post published when your audience is offline may receive far less attention than the same post published at a better time.</p>
              <p>With scheduling, you do not have to stop what you are doing just because it is “time to post.”</p>
              <div className="scheduling-pair"><div><span className="eyebrow">Your time</span><h3>Prepare your content</h3><p>When it is convenient for you.</p></div><div><span className="eyebrow">Their time</span><h3>Publish your content</h3><p>When it is convenient for your audience.</p></div></div>
            </section>

            <section id="review" aria-labelledby="review-title">
              <span className="scheduling-number" aria-hidden="true">03 / PEACE OF MIND</span>
              <h2 id="review-title">Last-minute posting creates expensive mistakes.</h2>
              <p>Posting in a rush makes it easier to publish:</p>
              <ul className="scheduling-checklist">{["Typographical errors", "Incorrect dates", "Wrong pricing", "Broken links", "Missing hashtags", "Outdated promotions", "The wrong image", "Content from the wrong brand or account"].map(item => <li key={item}><span aria-hidden="true">□</span>{item}</li>)}</ul>
              <p>Sometimes a small mistake is harmless. Sometimes thousands of people see it before you notice.</p>
              <div className="scheduling-takeaway"><span aria-hidden="true">✓</span><p>Scheduling gives you time to prepare, review, and organize your content before it becomes public.</p></div>
            </section>

            <section id="visibility" aria-labelledby="visibility-title">
              <span className="scheduling-number" aria-hidden="true">04 / VISIBILITY</span>
              <h2 id="visibility-title">Silence can cost more than you think.</h2>
              <p>A potential customer may visit your social profile before visiting your website.</p>
              <p>If the last post is weeks or months old, they may wonder:</p>
              <blockquote>“Is this business still active?”</blockquote>
              <p>Your social presence is part of your digital storefront.</p>
              <p>A steady stream of useful, relevant content helps your business look active, responsive, and ready for customers.</p>
            </section>

            <section id="system" aria-labelledby="system-title">
              <span className="scheduling-number" aria-hidden="true">05 / MORE HEADSPACE</span>
              <h2 id="system-title">Stop running your business around social media.</h2>
              <p>You should not have to remember:</p>
              <ul className="scheduling-reminders"><li>“I need to post on Instagram at 7 PM.”</li><li>“Did we already publish this on Facebook?”</li><li>“Was this supposed to go out today or tomorrow?”</li></ul>
              <div className="scheduling-takeaway"><span aria-hidden="true">◷</span><p>Your business should run on a system — not reminders in your head.</p></div>
            </section>
          </div>
        </div>
      </div>

      <section className="band scheduling-outro" aria-labelledby="scheduling-outro-title">
        <div className="band-inner narrow">
          <p className="hero-badge"><span aria-hidden="true">✦</span> Simino Social</p>
          <h2 id="scheduling-outro-title" className="section-title">Plan once. Publish on time.<span className="hero-gradient-text">Stay visible.</span></h2>
          <p className="lede"><strong>Simino Social helps you create, organize, schedule, and publish your social media content from one place.</strong></p>
          <div className="scheduling-next-steps"><p><span>01</span>Prepare your content ahead of time.</p><p><span>02</span>Choose when it should be published.</p><p><span>03</span>Keep your brand active even when you are busy doing something else.</p></div>
          <p>Because the biggest risk in social media is not always posting the wrong thing.</p>
          <p className="scheduling-closing">Sometimes it is not showing up at all.</p>
          <div className="hero-cta"><Link to="/pricing" className="btn-link primary lg">Explore scheduling plans <span aria-hidden="true">→</span></Link><Link to="/how-it-works" className="btn-link lg">See how it works</Link></div>
          <p className="scheduling-signoff">Schedule smarter. Publish on time. Stay visible.</p>
          <p>Your customers are scrolling.<br /><strong>Make sure they see you — not just your competitors.</strong></p>
        </div>
      </section>
    </article>
  );
}
